import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Slide,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { colors } from "../../theme";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f1f1;
`;

const GradientBadge = styled.div`
  background: ${colors.gradients.primary};
  color: white;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const StepContent = styled(Box)`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionCard = styled(Box)`
  border: 1px solid #f0f0f0;
  border-radius: 16px;
  padding: 16px;
  background: #fafafa;
`;

const StepActions = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 20px;
  border-top: 1px solid #f1f1f1;
`;

export const OnboardingModal = observer(({ controller }) => {
  const epicIss = process.env.REACT_APP_EPIC_ISS || "";
  const isMyChartEnabled = false;

  useEffect(() => {
    if (!isMyChartEnabled && controller.data.dataSource !== "manual") {
      controller.setTopLevel("dataSource", "manual");
    }
  }, [controller, controller.data.dataSource, isMyChartEnabled]);

  useEffect(() => {
    if (!controller.open) return;
    controller.startIfNeeded(true);
    controller.loadOnboardingOptions();
  }, [controller, controller.open]);
  const previewDoc = (html) => {
    if (!html) return "No preview available.";
    const text = html
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.length > 240 ? `${text.slice(0, 240)}...` : text;
  };

  const selectedDataSource = isMyChartEnabled
    ? controller.data.dataSource
    : "manual";

  const steps = useMemo(
    () => [
      { key: "intro", label: "Welcome" },
      { key: "dataSource", label: "Choose data source" },
      {
        key: selectedDataSource === "mychart" ? "mychart" : "manual",
        label:
          selectedDataSource === "mychart"
            ? "Connect MyChart"
            : "Manual lab entry",
      },
      { key: "bodyMetrics", label: "Body metrics" },
      { key: "preferences", label: "Food preferences" },
      { key: "goals", label: "Health goals" },
      { key: "review", label: "Review & submit" },
    ],
    [selectedDataSource]
  );

  const activeStep = Math.min(
    controller.activeStep,
    Math.max(steps.length - 1, 0)
  );
  const stepKey = steps[activeStep]?.key || "intro";
  const favoriteFoodOptions = controller.onboardingOptions.favoriteFoods || [];
  const allergyOptions = controller.onboardingOptions.allergies || [];
  const healthGoalOptions = controller.onboardingOptions.healthGoals || [];

  const handleNext = () => {
    const validation = controller.validateStep(stepKey);
    if (validation) {
      controller.error = validation;
      return;
    }
    controller.error = "";
    controller.nextStep();
  };

  const handleSaveDraft = () => controller.submit({ completed: false });
  const handleComplete = () => controller.submit({ completed: true });

  const togglePreference = (field, value) => {
    const current = controller.data.foodPreferences[field] || [];
    if (current.includes(value)) {
      controller.setField(
        "foodPreferences",
        field,
        current.filter((item) => item !== value)
      );
      return;
    }
    controller.setField("foodPreferences", field, [...current, value]);
  };

  const toggleGoal = (goalId) => {
    const goals = controller.data.healthGoals.goals || [];
    if (goals.includes(goalId)) {
      controller.setField(
        "healthGoals",
        "goals",
        goals.filter((g) => g !== goalId)
      );
    } else {
      controller.setField("healthGoals", "goals", [...goals, goalId]);
    }
  };

  const normalizeGoalOption = (goal) =>
    typeof goal === "string" ? { id: goal, label: goal, helper: "" } : goal;

  const renderIntro = () => (
    <SectionCard sx={{ background: colors.gradients.secondary, color: "#fff" }}>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>
        Culinary IQ is your AI co-pilot for nutrition and health.
      </Typography>
      <Typography sx={{ mt: 1, opacity: 0.9 }}>
        We'll personalize meal plans, biomonitoring, and grocery guidance to
        your goals. This quick tour sets up your data sources and health
        targets.
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          flexWrap: "wrap",
        }}
      >
        <GradientBadge>Guided setup</GradientBadge>
        <GradientBadge>Health-grade privacy</GradientBadge>
        <GradientBadge>Restart anytime</GradientBadge>
      </Box>
    </SectionCard>
  );

  const renderDataSource = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          How should we bring in your biomonitoring data?
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <ToggleButtonGroup
          value={selectedDataSource}
          exclusive
          onChange={(_, value) => {
            if (!value || (!isMyChartEnabled && value === "mychart")) return;
            controller.setTopLevel("dataSource", value);
          }}
          fullWidth
          color="primary"
        >
          <ToggleButton
            value="mychart"
            sx={{ flex: 1, p: 2 }}
            disabled={!isMyChartEnabled}
          >
            <Box textAlign="left">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  MyChart (YNHHS)
                </Typography>
                <Chip label="Coming soon" size="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Secure SSO into Yale New Haven Health to sync labs.
              </Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="manual" sx={{ flex: 1, p: 2 }}>
            <Box textAlign="left">
              <Typography variant="subtitle1" fontWeight={700}>
                Manual entry
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter key biomonitoring values yourself.
              </Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );

  const renderMyChart = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Connect to Yale New Haven Health MyChart
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We'll redirect you to Epic's sandbox MyChart login. Approve access,
          and you'll land back here with labs ready to import.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} size={12}>
        <FormControlLabel
          control={
            <Switch
              checked={controller.data.myChart.consent}
              onChange={(e) =>
                controller.setField("myChart", "consent", e.target.checked)
              }
              disabled={!isMyChartEnabled}
            />
          }
          label="I consent to import my lab data"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" color="text.secondary">
          Toggle consent to enable connection. We'll sync labs automatically.
        </Typography>
        <br />
        <Button
          variant="contained"
          startIcon={<LaunchIcon />}
          onClick={() => controller.startMyChartConnect(epicIss)}
          sx={{
            background: colors.gradients.primary,
            p: 1.4,
            fontWeight: 700,
          }}
          disabled={
            !isMyChartEnabled ||
            controller.syncingMyChart ||
            !controller.data.myChart.consent
          }
          fullWidth
        >
          {!isMyChartEnabled
            ? "MyChart coming soon"
            : controller.syncingMyChart
            ? "Syncing labs..."
            : "Connect MyChart (sandbox)"}
        </Button>
        <br />
        <br />
        <SectionCard>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Connection status
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}>
            <Chip
              label={
                controller.data.myChart.status === "connected"
                  ? "MyChart sync successful"
                  : controller.data.myChart.status === "error"
                  ? "MyChart sync failed"
                  : "MyChart not synced yet"
              }
              color={
                controller.data.myChart.status === "connected"
                  ? "success"
                  : controller.data.myChart.status === "error"
                  ? "error"
                  : "default"
              }
              variant={
                controller.data.myChart.status === "connected"
                  ? "filled"
                  : "outlined"
              }
            />
          </Box>
        </SectionCard>
      </Grid>
      {controller.data.labs.documents?.length > 0 && (
        <Grid item xs={12}>
          <SectionCard>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Lab documents
            </Typography>
            <Grid container spacing={2}>
              {controller.data.labs.documents.slice(0, 3).map((doc) => (
                <Grid
                  item
                  xs={12}
                  md={4}
                  key={doc.id || doc.documentReferenceId}
                >
                  <Box
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      background: "#fff",
                    }}
                  >
                    <Typography fontWeight={700}>
                      {doc.title || "Lab document"}
                    </Typography>
                    {doc.date && (
                      <Typography variant="body2" color="text.secondary">
                        Date: {new Date(doc.date).toLocaleDateString()}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {previewDoc(doc.html)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>
      )}
      <Grid item xs={12}>
        <SectionCard>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Lab values detected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Auto-filled from MyChart; you can adjust if something looks off.
          </Typography>
          {renderManualLabs()}
        </SectionCard>
      </Grid>
    </Grid>
  );

  const renderManualLabs = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} size={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Enter your key biomonitoring values
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These values help tailor recommendations. Units are pre-set to match
          the dashboard (mg/dL for cholesterol, % for A1C).
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Total Cholesterol (mg/dL)"
          value={controller.data.labs.cholesterolTotal}
          onChange={(e) =>
            controller.setField("labs", "cholesterolTotal", e.target.value)
          }
          type="number"
          fullWidth
          required={selectedDataSource === "manual"}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="LDL (mg/dL)"
          value={controller.data.labs.ldl}
          onChange={(e) => controller.setField("labs", "ldl", e.target.value)}
          type="number"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="HDL (mg/dL)"
          value={controller.data.labs.hdl}
          onChange={(e) => controller.setField("labs", "hdl", e.target.value)}
          type="number"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Triglycerides (mg/dL)"
          value={controller.data.labs.triglycerides}
          onChange={(e) =>
            controller.setField("labs", "triglycerides", e.target.value)
          }
          type="number"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="A1C (%)"
          value={controller.data.labs.a1c}
          onChange={(e) => controller.setField("labs", "a1c", e.target.value)}
          type="number"
          fullWidth
          required={selectedDataSource === "manual"}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Systolic (mmHg)"
          value={controller.data.labs.systolic}
          onChange={(e) =>
            controller.setField("labs", "systolic", e.target.value)
          }
          type="number"
          fullWidth
          required={selectedDataSource === "manual"}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Diastolic (mmHg)"
          value={controller.data.labs.diastolic}
          onChange={(e) =>
            controller.setField("labs", "diastolic", e.target.value)
          }
          type="number"
          fullWidth
          required={selectedDataSource === "manual"}
        />
      </Grid>
    </Grid>
  );

  const renderBodyMetrics = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} size={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Body metrics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A quick snapshot to set safe targets and tracking.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <SectionCard>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Weight
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Current weight (lbs)"
                value={controller.data.bodyMetrics.weightLbs}
                onChange={(e) =>
                  controller.setField(
                    "bodyMetrics",
                    "weightLbs",
                    e.target.value
                  )
                }
                type="number"
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target weight (lbs)"
                value={controller.data.bodyMetrics.targetWeightLbs}
                onChange={(e) =>
                  controller.setField(
                    "bodyMetrics",
                    "targetWeightLbs",
                    e.target.value
                  )
                }
                type="number"
                fullWidth
              />
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <SectionCard>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Height
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Feet"
                value={controller.data.bodyMetrics.heightFeet}
                onChange={(e) =>
                  controller.setField(
                    "bodyMetrics",
                    "heightFeet",
                    e.target.value
                  )
                }
                type="number"
                fullWidth
                placeholder="e.g., 5"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Inches"
                value={controller.data.bodyMetrics.heightInchesOnly}
                onChange={(e) =>
                  controller.setField(
                    "bodyMetrics",
                    "heightInchesOnly",
                    e.target.value
                  )
                }
                type="number"
                fullWidth
                placeholder="e.g., 10"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Or cm"
                value={controller.data.bodyMetrics.heightCm}
                onChange={(e) =>
                  controller.setField("bodyMetrics", "heightCm", e.target.value)
                }
                type="number"
                fullWidth
                placeholder="175"
              />
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>
    </Grid>
  );

  const renderPreferences = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} size={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Food preferences
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose favorites and allergies so we can tailor your plan.
        </Typography>
      </Grid>
      {controller.optionsError && (
        <Grid item xs={12}>
          <Alert severity="warning">{controller.optionsError}</Alert>
        </Grid>
      )}
      <Grid item xs={12} md={6} size={12}>
        <SectionCard>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Favorite foods
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pick a few staples you love.
          </Typography>
          {controller.optionsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading options...
            </Typography>
          ) : favoriteFoodOptions.length ? (
            <Grid container spacing={1}>
              {favoriteFoodOptions.map((food) => (
                <Grid item xs={12} sm={6} key={food}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(
                          controller.data.foodPreferences.favoriteFoods || []
                        ).includes(food)}
                        onChange={() => togglePreference("favoriteFoods", food)}
                      />
                    }
                    label={food}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No favorite food options available yet.
            </Typography>
          )}
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={6} size={12}>
        <SectionCard>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Allergies
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select any that apply so we can filter recipes.
          </Typography>
          {controller.optionsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading options...
            </Typography>
          ) : allergyOptions.length ? (
            <Grid container spacing={1}>
              {allergyOptions.map((allergy) => (
                <Grid item xs={12} sm={6} key={allergy}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(
                          controller.data.foodPreferences.allergies || []
                        ).includes(allergy)}
                        onChange={() => togglePreference("allergies", allergy)}
                      />
                    }
                    label={allergy}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No allergy options available yet.
            </Typography>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  );

  const renderGoals = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} size={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Health goals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose what you want to improve most.
        </Typography>
      </Grid>
      <Grid item xs={12} size={6}>
        <SectionCard>
          {controller.optionsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading options...
            </Typography>
          ) : healthGoalOptions.length ? (
            <Grid container spacing={2}>
              {healthGoalOptions.map((goal) => {
                const normalizedGoal = normalizeGoalOption(goal);
                return (
                  <Grid item xs={12} sm={6} key={normalizedGoal.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(
                            controller.data.healthGoals.goals || []
                          ).includes(normalizedGoal.id)}
                          onChange={() => toggleGoal(normalizedGoal.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography fontWeight={700}>
                            {normalizedGoal.label}
                          </Typography>
                          {normalizedGoal.helper && (
                            <Typography variant="body2" color="text.secondary">
                              {normalizedGoal.helper}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No health goal options available yet.
            </Typography>
          )}
        </SectionCard>
      </Grid>
      <Grid item xs={12} size={6}>
        <TextField
          label="Notes or targets"
          value={controller.data.healthGoals.notes}
          onChange={(e) =>
            controller.setField("healthGoals", "notes", e.target.value)
          }
          fullWidth
          multiline
          minRows={2}
          placeholder="Optional notes, e.g., target BMI or diet preference."
        />
      </Grid>
    </Grid>
  );

  const renderReview = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} size={12}>
        <Alert severity="info" sx={{ mb: 1 }}>
          Save and finish to unlock the dashboard. You can revisit from Settings
          later.
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <SectionCard>
          <Typography fontWeight={700}>Data source</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedDataSource === "mychart"
              ? "MyChart (YNHHS) with consent: " +
                (controller.data.myChart.consent ? "Yes" : "No")
              : "Manual entry"}
          </Typography>
        </SectionCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SectionCard>
          <Typography fontWeight={700}>Biomonitoring</Typography>
          <Typography color="text.secondary">
            Cholesterol: {controller.data.labs.cholesterolTotal || "n/a"} mg/dL
          </Typography>
          <Typography color="text.secondary">
            A1C: {controller.data.labs.a1c || "n/a"}%
          </Typography>
          <Typography color="text.secondary">
            BP: {controller.data.labs.systolic || "?"}/
            {controller.data.labs.diastolic || "?"} mmHg
          </Typography>
        </SectionCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SectionCard>
          <Typography fontWeight={700}>Body metrics</Typography>
          <Typography color="text.secondary">
            Weight: {controller.data.bodyMetrics.weightLbs || "n/a"} lbs
          </Typography>
          <Typography color="text.secondary">
            Height:{" "}
            {controller.data.bodyMetrics.heightCm
              ? `${controller.data.bodyMetrics.heightCm} cm`
              : controller.data.bodyMetrics.heightFeet
              ? `${controller.data.bodyMetrics.heightFeet}'${
                  controller.data.bodyMetrics.heightInchesOnly || 0
                }"`
              : "n/a"}
          </Typography>
          <Typography color="text.secondary">
            Target weight:{" "}
            {controller.data.bodyMetrics.targetWeightLbs || "n/a"} lbs
          </Typography>
        </SectionCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SectionCard>
          <Typography fontWeight={700}>Preferences</Typography>
          <Typography color="text.secondary">
            Favorites:{" "}
            {(controller.data.foodPreferences.favoriteFoods || []).join(", ") ||
              "n/a"}
          </Typography>
          <Typography color="text.secondary">
            Allergies:{" "}
            {(controller.data.foodPreferences.allergies || []).join(", ") ||
              "n/a"}
          </Typography>
        </SectionCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SectionCard>
          <Typography fontWeight={700}>Goals</Typography>
          <Typography color="text.secondary">
            {(controller.data.healthGoals.goals || []).length
              ? (controller.data.healthGoals.goals || []).join(", ")
              : "None selected"}
          </Typography>
          {controller.data.healthGoals.notes && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Notes: {controller.data.healthGoals.notes}
            </Typography>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  );

  const renderStepBody = () => {
    switch (stepKey) {
      case "intro":
        return renderIntro();
      case "dataSource":
        return renderDataSource();
      case "mychart":
        return renderMyChart();
      case "manual":
        return renderManualLabs();
      case "bodyMetrics":
        return renderBodyMetrics();
      case "preferences":
        return renderPreferences();
      case "goals":
        return renderGoals();
      case "review":
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={controller.open}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => controller.setOpen(false)}
      disableEscapeKeyDown={!controller.canClose}
    >
      <Header>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Guided Onboarding
          </Typography>
          {controller.requireCompletion && (
            <GradientBadge>Required on first sign-in</GradientBadge>
          )}
        </Box>
        <Button
          onClick={() => controller.setOpen(false)}
          startIcon={<CloseIcon />}
          color="inherit"
          disabled={!controller.canClose}
        >
          Close
        </Button>
      </Header>

      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <LinearProgress
          variant="determinate"
          value={((activeStep + 1) / steps.length) * 100}
          sx={{ mt: 1, height: 8, borderRadius: 4 }}
        />
      </Box>

      <StepContent>{renderStepBody()}</StepContent>

      <Divider />

      {(controller.error || controller.message) && (
        <Box sx={{ px: 3, pt: 2 }}>
          {controller.error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {controller.error}
            </Alert>
          )}
          {controller.message && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {controller.message}
            </Alert>
          )}
        </Box>
      )}

      <StepActions>
        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep > 0 && (
            <Button
              onClick={() => controller.previousStep()}
              disabled={controller.saving}
            >
              Back
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            startIcon={<SaveAltIcon />}
            disabled={controller.saving}
          >
            Save progress
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={controller.saving}
              sx={{ background: colors.gradients.primary }}
            >
              Continue
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleComplete}
              startIcon={<CheckCircleOutlineIcon />}
              disabled={controller.saving}
              sx={{ background: colors.gradients.primary }}
            >
              Finish onboarding
            </Button>
          )}
        </Box>
      </StepActions>
    </Dialog>
  );
});

export default OnboardingModal;
